import { msg } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react';
import { formatNumber } from '../i18n/format';
import { Empty } from 'antd';
import { MessageOutlined, EditOutlined, DownOutlined, RightOutlined } from '@ant-design/icons';
import TaskHeartbeatControl from './TaskHeartbeatControl';
import { ExecutionUsageChip } from './ExecutionUsageChip';
import { STATUS_CONFIG, STATUS_COLUMNS } from '../lib/taskStatus';
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  rectIntersection,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  type DragStartEvent,
  type DragEndEvent,
  type CollisionDetection,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { UserAvatar } from './UserAvatar';
import { UserName } from './UserName';
import ParticipantAvatars from './ParticipantAvatars';
import { useTaskStore } from '../stores/useTaskStore';
import * as api from '../lib/api';
import type { ChannelMember } from '../lib/api';
import { useState, useCallback } from 'react';

const COLLAPSED_COLUMNS_STORAGE_KEY = 'borgee:tasks:collapsedColumns';

// The terminal columns hold history rather than work in flight, so they start collapsed.
const DEFAULT_COLLAPSED_COLUMNS: Record<string, boolean> = { done: true, cancelled: true };

// The stored map is layered over the defaults rather than replacing them: it records the
// columns the user decided about, and any column they never touched keeps its default.
function loadCollapsedColumns(): Record<string, boolean> {
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(COLLAPSED_COLUMNS_STORAGE_KEY) || '{}');
    if (typeof stored !== 'object' || stored === null) return { ...DEFAULT_COLLAPSED_COLUMNS };
    return { ...DEFAULT_COLLAPSED_COLUMNS, ...stored };
  } catch {
    return { ...DEFAULT_COLLAPSED_COLUMNS };
  }
}

function saveCollapsedColumns(collapsed: Record<string, boolean>) {
  localStorage.setItem(COLLAPSED_COLUMNS_STORAGE_KEY, JSON.stringify(collapsed));
}

interface Props {
  tasks: api.Task[];
  members: ChannelMember[];
  channelMap?: Record<string, string>;
  sourceMap?: Record<string, { guildName: string; channelName: string }>;
  channelId?: string;
  activeThreadId?: string;
  onTaskClick?: (task: api.Task) => void;
  onTaskTitleClick?: (task: api.Task) => void;
  onEditTask?: (task: api.Task) => void;
  readOnly?: boolean;
}

export default function TasksBoardView({ tasks, members, channelMap, sourceMap, channelId, activeThreadId, onTaskClick, onTaskTitleClick, onEditTask, readOnly = false }: Props) {
  const { i18n } = useLingui();
  const [activeTask, setActiveTask] = useState<api.Task | null>(null);
  const [collapsedColumns, setCollapsedColumns] = useState<Record<string, boolean>>(loadCollapsedColumns);

  const toggleColumn = useCallback((key: string) => {
    setCollapsedColumns(prev => {
      const next = { ...prev, [key]: !prev[key] };
      saveCollapsedColumns(next);
      return next;
    });
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const collisionDetection: CollisionDetection = useCallback((args) => {
    const pointerCollisions = pointerWithin(args);
    const columnHit = pointerCollisions.find(c => String(c.id).startsWith('column:'));
    if (columnHit) return [columnHit];
    const rectCollisions = rectIntersection(args);
    if (rectCollisions.length > 0) return rectCollisions;
    return pointerCollisions;
  }, []);

  const handleDragStart = (event: DragStartEvent) => {
    const task = tasks.find(t => t.id === event.active.id);
    setActiveTask(task ?? null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveTask(null);
    const { active, over } = event;
    if (!over) return;

    const taskId = active.id as string;
    const overId = over.id as string;

    let newStatus: string | undefined;
    const colPrefix = 'column:';
    if (overId.startsWith(colPrefix)) {
      newStatus = overId.slice(colPrefix.length);
    } else if (STATUS_COLUMNS.some(c => c.key === overId)) {
      newStatus = overId;
    } else {
      newStatus = (over.data.current as any)?.sortable?.containerId as string | undefined;
    }

    if (newStatus && STATUS_COLUMNS.some(c => c.key === newStatus)) {
      const task = tasks.find(t => t.id === taskId);
      if (task && task.status !== newStatus) {
        useTaskStore.getState().upsertTask({ ...task, status: newStatus as api.Task['status'], updated_at: Date.now() });
        await api.updateTask(taskId, { status: newStatus });
      }
    }
  };

  return (
    <DndContext
      sensors={readOnly ? [] : sensors}
      collisionDetection={collisionDetection}
      onDragStart={readOnly ? undefined : handleDragStart}
      onDragEnd={readOnly ? undefined : handleDragEnd}
    >
      <div className="tasks-kanban">
        {STATUS_COLUMNS.map(col => {
          const colTasks = tasks.filter(t => t.status === col.key);
          return (
            <KanbanColumn
              key={col.key}
              id={col.key}
              label={i18n._(col.label)}
              color={col.color}
              icon={col.icon}
              count={colTasks.length}
              collapsed={!!collapsedColumns[col.key]}
              onToggleCollapsed={() => toggleColumn(col.key)}
            >
              <SortableContext id={col.key} items={colTasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
                {colTasks.length === 0 ? (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="" style={{ margin: '20px 0' }} />
                ) : (
                  colTasks.map(task => (
                    <SortableTaskCard
                      key={task.id}
                      task={task}
                      members={members}
                      channelMap={!channelId ? channelMap : undefined}
                      sourceMap={!channelId ? sourceMap : undefined}
                      active={task.thread_id === activeThreadId}
                      onClick={() => onTaskClick?.(task)}
                      onTitleClick={() => onTaskTitleClick?.(task)}
                      onEdit={onEditTask ? () => onEditTask(task) : undefined}
                      readOnly={readOnly}
                    />
                  ))
                )}
              </SortableContext>
            </KanbanColumn>
          );
        })}
      </div>
      <DragOverlay>
        {activeTask && <TaskCardContent task={activeTask} members={members} channelMap={!channelId ? channelMap : undefined} sourceMap={!channelId ? sourceMap : undefined} />}
      </DragOverlay>
    </DndContext>
  );
}

function KanbanColumn({ id, label, color, count, icon, collapsed, onToggleCollapsed, children }: {
  id: string; label: string; color: string; count: number; icon?: React.ReactNode;
  collapsed: boolean; onToggleCollapsed: () => void; children: React.ReactNode;
}) {
  const { i18n } = useLingui();
  const { setNodeRef, isOver } = useDroppable({ id: `column:${id}` });
  return (
    <div
      ref={setNodeRef}
      className={`tasks-kanban-column${collapsed ? ' tasks-kanban-column-collapsed' : ''}${isOver ? ' tasks-kanban-column-over' : ''}`}
    >
      <div className="tasks-kanban-column-header">
        <button
          type="button"
          className="tasks-kanban-collapse-toggle"
          onClick={onToggleCollapsed}
          aria-expanded={!collapsed}
          aria-label={collapsed ? i18n._(msg`Expand ${label} column`) : i18n._(msg`Collapse ${label} column`)}
        >
          {collapsed ? <RightOutlined aria-hidden="true" /> : <DownOutlined aria-hidden="true" />}
        </button>
        <span className="tasks-kanban-column-label" style={{ color }}>
          <span className="tasks-kanban-column-icon">{icon}</span>
          <span className="tasks-kanban-column-name">{label}</span>
        </span>
        <span className="tasks-kanban-count">{formatNumber(i18n, count)}</span>
      </div>
      {!collapsed && (
        <div className="tasks-kanban-cards">
          {children}
        </div>
      )}
    </div>
  );
}

function SortableTaskCard({ task, members, channelMap, sourceMap, active, onClick, onTitleClick, onEdit, readOnly }: {
  task: api.Task; members: ChannelMember[]; channelMap?: Record<string, string>; sourceMap?: Record<string, { guildName: string; channelName: string }>; active?: boolean; onClick: () => void; onTitleClick: () => void; onEdit?: () => void; readOnly?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <TaskCardContent task={task} members={members} channelMap={channelMap} sourceMap={sourceMap} active={active} onThreadClick={onClick} onTitleClick={onTitleClick} onEditClick={onEdit} readOnly={readOnly} />
    </div>
  );
}

function TaskCardContent({ task, members, channelMap, sourceMap, active, onThreadClick, onTitleClick, onEditClick, readOnly }: {
  task: api.Task;
  members: ChannelMember[];
  channelMap?: Record<string, string>;
  sourceMap?: Record<string, { guildName: string; channelName: string }>;
  active?: boolean;
  onThreadClick?: () => void;
  onTitleClick?: () => void;
  onEditClick?: () => void;
  readOnly?: boolean;
}) {
  const { i18n } = useLingui();
  const assignee = task.assignee;
  const channelName = channelMap?.[task.channel_id];
  const source = sourceMap?.[task.channel_id];
  const participants = task.participant_profiles?.map(person => ({ user_id: person.id, display_name: person.display_name, avatar_url: person.avatar_url ?? undefined }))
    ?? task.participants?.map(userId => {
      const member = members.find(candidate => candidate.user_id === userId);
      return { user_id: userId, display_name: member?.display_name ?? userId, avatar_url: member?.avatar_url ?? undefined };
    }) ?? [];
  return (
    <div className={`task-card${active ? ' task-card-active' : ''}`} onClick={() => onThreadClick?.()}>
      <div className="task-card-header">
        <div className="task-card-title">
          <span className="task-card-context">{source ? <span className="task-card-channel">{source.guildName}/#{source.channelName}/</span> : channelName && <span className="task-card-channel">#{channelName}/</span>}<span className="task-card-seq">#{task.seq}</span></span>
          <button type="button" className="task-title-link task-card-title-link" title={task.title} onClick={event => { event.stopPropagation(); onTitleClick?.(); }}>{task.title}</button>
        </div>
        <div className="task-card-actions">
          {task.thread_id && onThreadClick && (
            <button type="button" className="task-card-btn" onClick={e => { e.stopPropagation(); onThreadClick(); }} aria-label={i18n._(msg`Open thread`)}>
              <MessageOutlined aria-hidden="true" />
            </button>
          )}
          {onEditClick && (
            <button type="button" className="task-card-btn" onClick={e => { e.stopPropagation(); onEditClick(); }} aria-label={i18n._(msg`Edit task`)}>
              <EditOutlined aria-hidden="true" />
            </button>
          )}
        </div>
      </div>
      <div className="task-card-footer">
        <span className="task-card-people">{assignee && <>
          <UserAvatar userId={assignee.id} displayName={assignee.display_name} size={20} />
          <span className="task-card-assignee-name"><UserName displayName={assignee.display_name} role={assignee.role} /></span>
        </>}{participants.length > 0 && <ParticipantAvatars participants={participants} size={18} max={3} />}</span>
        {!readOnly && <TaskHeartbeatControl task={task} size={16} className="task-card-heartbeat-icon" />}
        {task.usage_summary && task.usage_summary.coverage !== 'unavailable' && <span className="task-card-usage"><ExecutionUsageChip resourceId={task.id} subscriptionId={task.thread_id} scope="task" deferred initialSummary={task.usage_summary} /></span>}
      </div>
    </div>
  );
}
